import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFetchData, useCreateData, useUpdateData } from 'services/queries';
import { useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
  FormHelperText,
} from '@mui/material';
import { CATEGORY_CHOICES } from 'constants';

const TemplateItemForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditMode = !!id;
  
  const resource = 'additional-cost/template-items';
  const subcategoryResource = 'additional-cost/subcategory';
  
  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    description: '',
    amount: '',
  });
  
  const [errors, setErrors] = useState({});
  
  // Fetch existing item data if in edit mode
  const { data: itemData, isLoading: isLoadingItem } = useFetchData(
    `${resource}/${id}`,
    isEditMode
  );
  
  // Fetch subcategories filtered by selected category
  const { data: subcategories, refetch: refetchSubcategories } = useFetchData(
    formData.category ? `${subcategoryResource}?category=${formData.category}` : null,
    !!formData.category
  );
  
  // Create and update mutations
  const createMutation = useCreateData(resource, [resource], {
    showNotification: true,
  });
  
  const updateMutation = useUpdateData(resource, [resource], {
    showNotification: true,
  });
  
  // Load item data when editing
  useEffect(() => {
    if (itemData && isEditMode) {
      setFormData({
        category: itemData.category || '',
        subcategory: itemData.subcategory || '',
        description: itemData.description || '',
        amount: itemData.amount || '',
      });
    }
  }, [itemData, isEditMode]);
  
  // Refetch subcategories when category changes
  useEffect(() => {
    if (formData.category) {
      refetchSubcategories();
    }
  }, [formData.category, refetchSubcategories]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear subcategory when category changes
    if (name === 'category' && formData.category !== value) {
      setFormData(prev => ({
        ...prev,
        subcategory: '',
      }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    return newErrors;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    const dataToSubmit = {
      ...formData,
      subcategory: formData.subcategory || null,
      amount: formData.amount || 0,
    };
    
    const mutation = isEditMode ? updateMutation : createMutation;
    const mutationParams = isEditMode ? 
      { id, newData: dataToSubmit } : 
      dataToSubmit;
    
    mutation.mutate(mutationParams, {
      onSuccess: (data) => {
        queryClient.invalidateQueries([resource]);
        if (!isEditMode && data?.id) {
          // Redirect to edit page after create
          navigate(`/admin/settings/additional-costs/template/edit/${data.id}`);
        }
        // Stay on the same page after update
      },
      onError: (error) => {
        if (error.response?.data) {
          const apiErrors = error.response.data;
          const formattedErrors = {};
          
          Object.keys(apiErrors).forEach(key => {
            if (Array.isArray(apiErrors[key])) {
              formattedErrors[key] = apiErrors[key][0];
            } else {
              formattedErrors[key] = apiErrors[key];
            }
          });
          
          setErrors(formattedErrors);
        }
      },
    });
  };
  
  const handleCancel = () => {
    navigate('/admin/settings/additional-costs');
  };
  
  if (isLoadingItem && isEditMode) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Card>
      <form onSubmit={handleSubmit}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            {isEditMode ? 'Edit Template Item' : 'Create Template Item'}
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth error={!!errors.category}>
              <InputLabel id="category-label">Category *</InputLabel>
              <Select
                labelId="category-label"
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                label="Category *"
                disabled={isEditMode} // Read-only in edit mode
              >
                {CATEGORY_CHOICES.map((choice) => (
                  <MenuItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && (
                <FormHelperText>{errors.category}</FormHelperText>
              )}
              {isEditMode && (
                <FormHelperText>Category cannot be changed after creation</FormHelperText>
              )}
            </FormControl>
            
            <FormControl fullWidth error={!!errors.subcategory}>
              <InputLabel id="subcategory-label">Subcategory</InputLabel>
              <Select
                labelId="subcategory-label"
                id="subcategory"
                name="subcategory"
                value={formData.subcategory}
                onChange={handleChange}
                label="Subcategory"
                disabled={!formData.category}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {subcategories && subcategories.map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>
                    {sub.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.subcategory && (
                <FormHelperText>{errors.subcategory}</FormHelperText>
              )}
              {!formData.category && (
                <FormHelperText>Select a category first</FormHelperText>
              )}
            </FormControl>
            
            <TextField
              fullWidth
              id="description"
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleChange}
              error={!!errors.description}
              helperText={errors.description}
              multiline
              rows={3}
            />
            
            <TextField
              fullWidth
              id="amount"
              name="amount"
              label="Amount ($)"
              type="number"
              value={formData.amount}
              onChange={handleChange}
              error={!!errors.amount}
              helperText={errors.amount || 'Optional: Enter the additional cost amount'}
              inputProps={{
                step: '0.01',
                min: '0'
              }}
            />
          </Box>
        </CardContent>
        
        <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
          <Button 
            onClick={handleCancel}
            disabled={createMutation.isLoading || updateMutation.isLoading}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={createMutation.isLoading || updateMutation.isLoading}
          >
            {createMutation.isLoading || updateMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              isEditMode ? 'Update' : 'Create'
            )}
          </Button>
        </CardActions>
      </form>
    </Card>
  );
};

export default TemplateItemForm;